import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroupInfo } from '../hooks/queries/useGroupInfo';
import { useGroupMembers } from '../hooks/queries/useGroupMembers';
import { useAddExpense } from '../hooks/mutations/useAddExpense';
import { useExpenseForm } from '../hooks/useExpenseForm';

// 컴포넌트 import
import ParticipantList from '../components/expense/ParticipantList';
import AmountInput from '../components/expense/AmountInput';
import TitleInput from '../components/expense/TitleInput';
import MemberSelect from '../components/expense/MemberSelect';
import SubmitButton from '../components/expense/SubmitButton';
import ExpenseTypeSelect from '../components/expense/ExpenseTypeSelect';
import { currencies, CurrencyCode } from '../constants/currencies';
import { useParsedCurrencyRates } from '../hooks/useParsedCurrencyRates';
import Header from '../components/Header';
import { getUserData } from '../lib/client';
import { toast } from 'react-hot-toast';

const AddExpensePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isLoadingRef = useRef(false);

  const currentUser = getUserData();

  const { data: group, isLoading: groupLoading, isError: groupError } = useGroupInfo(id);
  const { data: members, isLoading: membersLoading, isError: membersError } = useGroupMembers(id);
  const { currencyMap, isLoading: currenciesLoading, isError: currenciesError } = useParsedCurrencyRates();
  const isLoading = groupLoading || membersLoading || currenciesLoading;
  const isError = groupError || membersError || currenciesError;
  const [submitLoading, setSubmitLoading] = useState(false);
  const { mutateAsync: addExpense } = useAddExpense();
  const defaultCurrency = group?.default_currency as CurrencyCode;
  const lastCurrency = localStorage.getItem(id+'currency') as CurrencyCode;

  // useExpenseForm 훅 사용
  const {
    // 상태
    expenseType,
    splitType,
    multipliers,
    manualAmounts,
    formattedManualAmounts,
    amount,
    amountForDisplay,
    participants,
    paidBy,
    spentAt,
    errors,
    currency,
    exchangeRate,
    exchangeRateInput,
    // 함수
    register,
    handleSubmit,
    toggleParticipant,
    adjustMultiplier,
    handleManualAmountInputChange,
    handleManualAmountBlur,
    calculateShare,
    onInputChange,
    handleExpenseTypeChange,
    handleSplitTypeChange,
    getParticipantsWithAmount,
    validateManualAmounts,
    handleSpentAtChange,
    onInputBlur,
    onExchangeRateChange,
    onExchangeRateBlur
  } = useExpenseForm({
    members: members ?? [],
    currentUser,
    defaultCurrency,
    lastCurrency,
    currencyMap,
  });

  if (!id || !currentUser) {
    navigate('/', {replace: true});
  }

  const onSubmit = async (data: any) => {
    if (!id || !currentUser) return;
    if (isLoadingRef.current) return;
    
    // 다른 통화 선택 시 환율이 1인 경우 검증
    if (currency !== defaultCurrency && exchangeRate === 1) {
      toast.error('환율 정보를 확인해주세요.');
      return;
    }
    if (exchangeRate <= 0) {
      toast.error('환율을 입력해주세요.');
      return;
    }
    
    try {
      setSubmitLoading(true);
      isLoadingRef.current = true;
      
      if (expenseType === '지출기록') {
        // 수동 입력 방식일 경우 금액 검증
        if (splitType === 'amounts') {
          const validation = validateManualAmounts();
          if (!validation.isValid) {
            toast.error(validation.message);
            setSubmitLoading(false);
            return;
          }
        }
        
        // 참가자별 금액 계산하여 API 요청 형식으로 변환
        const participantsWithAmount = getParticipantsWithAmount();

        // 통화에 맞는 소수점 자릿수로 반올림
        const maxFractionDigits = currencies[defaultCurrency].maximumFractionDigits;
        const amountInDefaultCurrency = Number((data.amount * data.exchangeRate).toFixed(maxFractionDigits));
        const participantsWithAmountInDefaultCurrency = participantsWithAmount.map(participant => ({
          ...participant,
          amount: Number((participant.amount * data.exchangeRate).toFixed(maxFractionDigits)),
          amountInCurrency: Number(participant.amount.toFixed(currencies[currency].maximumFractionDigits))
        }));
        
        await addExpense({
          groupId: id,
          title: data.title,
          amount: amountInDefaultCurrency,
          currency: currency,
          amountInCurrency:amount,
          exchangeRate: exchangeRate,
          paidBy: data.paidBy,
          participants: participantsWithAmountInDefaultCurrency,
          isTransfer: false,
          splitType: splitType,
          spentAt: new Date(data.spentAt)
        });
      } else {
        // 송금기록 처리
        if (!data.receiverId) {
          toast.error('송금 받는 사람을 선택해주세요');
          setSubmitLoading(false);
          return;
        }

        // 통화에 맞는 소수점 자릿수로 반올림
        const amountInDefaultCurrency = Number((data.amount * data.exchangeRate).toFixed(currencies[defaultCurrency].maximumFractionDigits));
        
        // 송금 기록 추가 - 수신자만 참가자로 설정
        const participantsWithAmount = [{
          id: data.receiverId,
          amount: amountInDefaultCurrency,
          amountInCurrency:amount
        }];
        
        await addExpense({
          groupId: id,
          title: '개인송금',
          amount: amountInDefaultCurrency,
          currency: currency,
          amountInCurrency:amount,
          exchangeRate: exchangeRate,
          paidBy: data.paidBy,
          participants: participantsWithAmount,
          isTransfer: true,
          transferTo: data.receiverId,
          spentAt: new Date(data.spentAt)
        });
      }
      
      if (lastCurrency !== currency) {
        localStorage.setItem(id+'currency', currency);
      }
      // 성공 후 그룹 상세 페이지로 이동
      navigate(`/groups/${id}`, {replace: true});
    } catch (error: any) {
      toast.error(`오류: ${error.message}`);
    } finally {
      setSubmitLoading(false);
      isLoadingRef.current = false;
    }
  };

  if (isError) {
    return <div className="flex justify-center p-8 text-red-500">그룹 정보를 불러오는 중 오류가 발생했습니다.</div>;
  }

  return (
    <div className="container mx-auto px-4 pt-16 pb-8">
      <Header title={group?.name || '...'} showBackButton onBackClick={() => navigate(-1)} />
      <ExpenseTypeSelect 
        expenseType={expenseType}
        onChange={handleExpenseTypeChange}
      />
      <div className="max-w-screen-md mx-auto">
        {isLoading || !currency || !defaultCurrency ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-8 h-8 border-4 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* 금액 */}
            <AmountInput 
              currency={currency}
              formattedAmount={amountForDisplay}
              onInputChange={onInputChange}
              error={errors.amount?.message}
              register={register}
              currencyMap={currencyMap}
              onInputBlur={() => {onInputBlur(); 
                setTimeout(() => {
                  document.getElementById('title')?.focus();
                }, 100);
              }}
              autoFocus={true}
            />
              
            <input type="hidden" {...register('amount', { 
              required: '금액을 입력해주세요',
              min: { value: 1, message: '금액을 입력해주세요' },
              valueAsNumber: true
            })} />

            {/* 환율 입력 */}
            {currency !== defaultCurrency && (
              <div className="w-full transition-all duration-300 ease-in-out flex items-center -mt-2 ml-1 gap-1">
                <span className='w-[110px] text-gray-500 font-mono'>
                  {currency} 1 = {defaultCurrency}
                </span>
                <input 
                  type="text" 
                  id="exchangeRateInput"
                  className='ml-1 w-[calc(100%-114px)] font-mono-number'
                  value={exchangeRateInput}
                  onChange={onExchangeRateChange}
                  onKeyDown={(e)=>{
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={onExchangeRateBlur}
                />
                <input type="hidden" {...register('exchangeRate', {
                  value: exchangeRate,
                  min: { value: 0, message: '환율을 입력해주세요' },
                  valueAsNumber: true
                })} />
              </div>
            )}

            {/* 지출 설명 */}
            <TitleInput 
              register={register}
              disabled={expenseType === '송금기록'}
              error={errors.title?.message}
            />
            
            
            {/* 결제자/송금자와 날짜 입력 필드를 함께 배치 */}
            <div className="flex gap-2">
              {/* 결제자 또는 송금자 */}
              <div className="flex-1">
                <MemberSelect
                  id="paidBy"
                  label={expenseType === '송금기록' ? '보내는 사람' : '결제한 사람'}
                  members={members ?? []}
                  currentUser={currentUser}
                  register={register}
                  name="paidBy"
                  error={errors.paidBy?.message}
                />
              </div>
              
              {/* 날짜 입력 필드 */}
              <div className="flex-1 relative">
                <label>
                  날짜
                </label>
                <input
                  type="date"
                  id="spentAt"
                  className="block w-full"
                  value={spentAt}
                  {...register('spentAt')}
                  onChange={handleSpentAtChange}
                />
              </div>
            </div>
            
            {/* 송금 받는 사람 (송금기록 선택 시) */}
            {expenseType === '송금기록' && (
              <MemberSelect
                id="receiverId"
                label="받는 사람"
                members={members ?? []}
                currentUser={currentUser}
                register={register} 
                name="receiverId"
                error={errors.receiverId?.message}
                excludeId={paidBy}
                placeholder="선택하세요"
              />
            )}
            
            {/* 참가자 (지출기록 선택 시) */}
            {expenseType === '지출기록' && (
              <ParticipantList
                selectedCurrency={currency}
                members={members ?? []}
                participants={participants}
                currentUser={currentUser}
                splitType={splitType}
                amount={amount}
                multipliers={multipliers}
                manualAmounts={manualAmounts}
                formattedManualAmounts={formattedManualAmounts}
                calculateShare={calculateShare}
                toggleParticipant={toggleParticipant}
                adjustMultiplier={adjustMultiplier}
                handleManualAmountInputChange={handleManualAmountInputChange}
                handleManualAmountBlur={handleManualAmountBlur}
                onSplitTypeChange={handleSplitTypeChange}
              />
            )}
            {/* 제출 버튼 */}
            <div className='h-16'/>
            <SubmitButton 
              loading={submitLoading}
              expenseType={expenseType}
            />
          </form>
        )}
      </div>
    </div>
  );
};

export default AddExpensePage; 